import type {Artifact} from './artifact-core'
import {isArtifactHtmlPreviewEligible, isSupportedCss} from './html-preview-eligibility'

export type ArtifactHtmlPreviewPlan =
  | {kind: 'legacy'; previewEligible: boolean | undefined}
  | {kind: 'unsupported'}
  | {kind: 'prepared'; payload: ArtifactHtmlPreviewPayload; signature: string}

export interface ArtifactHtmlPreviewPayload {
  content: string
  inlineScripts: string
}

export interface ArtifactHtmlPreviewCacheEntry {
  absolutePath: string
  content: string | undefined
  writesSettled: boolean
  dependencies: Map<string, string | undefined>
  plan: ArtifactHtmlPreviewPlan
}

const HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml'
const UNSUPPORTED_ELEMENTS = new Set(['template', 'noscript', 'noframes'])
const INERT_SCRIPT_TYPES = new Set(['application/json', 'application/ld+json'])
const STYLESHEET_ATTRIBUTES = new Set(['rel', 'href', 'type'])
const SLOT_ATTRIBUTE = 'data-artifact-preview-slot'

function isRemoteStylesheetUrl(value: string): boolean {
  try {
    const url = new URL(value, 'about:blank')
    if (url.protocol !== 'https:' && url.protocol !== 'data:') return false
    const first = new URL(value, 'https://base-one.invalid/dir/')
    const second = new URL(value, 'https://base-two.invalid/other/')
    return first.href === second.href && (first.protocol === 'https:' || first.protocol === 'data:')
  } catch {
    return false
  }
}

function siblingStylesheetPath(entry: Artifact, href: string): string | undefined {
  let start = 0
  let end = href.length
  while (start < end && href.charCodeAt(start) <= 0x20) start++
  while (end > start && href.charCodeAt(end - 1) <= 0x20) end--
  const name = href.slice(start, end).replace(/^\.\//, '')
  if (!/^[A-Za-z0-9_-][A-Za-z0-9._-]*\.css$/.test(name)) return undefined
  const separator = Math.max(entry.absolutePath.lastIndexOf('/'), entry.absolutePath.lastIndexOf('\\'))
  return entry.absolutePath.slice(0, separator + 1) + name
}

function buildStylesheetLoader(styles: string[], html: Array<[string, string]>, body: Array<[string, string]>): string {
  const data = JSON.stringify({styles, html, body})
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
  return `(() => {
const data = ${data};
data.styles.forEach((css, index) => {
const slot = document.querySelector('style[data-artifact-preview-slot="' + index + '"]');
if (slot) slot.textContent = css;
});
for (const [target, attributes] of [[document.documentElement, data.html], [document.body, data.body]]) {
for (const [name, value] of attributes) {
try { target.setAttribute(name, value); } catch { continue; }
}
}
})();`
}

function buildPreviewPlan(
  entry: Artifact,
  catalog: readonly Artifact[],
  writesSettled: boolean,
  dependencies: Map<string, string | undefined>,
): ArtifactHtmlPreviewPlan {
  const original = entry.content ?? ''
  const legacy = (): ArtifactHtmlPreviewPlan => ({
    kind: 'legacy',
    previewEligible: isArtifactHtmlPreviewEligible(original),
  })
  if (typeof DOMParser === 'undefined') return legacy()
  let parsed: Document
  try {
    parsed = new DOMParser().parseFromString(original, 'text/html')
  } catch {
    return legacy()
  }

  const localStylesheets = [...parsed.querySelectorAll('link')].filter(link => {
    const href = link.getAttribute('href')
    return (
      link.namespaceURI === HTML_NAMESPACE &&
      !link.closest('noscript') &&
      (link.getAttribute('rel') ?? '').toLowerCase().split(/\s+/).includes('stylesheet') &&
      href !== null &&
      !isRemoteStylesheetUrl(href)
    )
  })
  if (localStylesheets.length === 0) return legacy()
  if (!writesSettled || entry.content === undefined) return {kind: 'unsupported'}

  for (const element of parsed.querySelectorAll('*')) {
    if (element.namespaceURI !== HTML_NAMESPACE || UNSUPPORTED_ELEMENTS.has(element.localName)) {
      return {kind: 'unsupported'}
    }
    if (
      [...element.attributes].some(attribute => attribute.name === SLOT_ATTRIBUTE || attribute.name.startsWith('on'))
    ) {
      return {kind: 'unsupported'}
    }
    if (
      element.localName === 'script' &&
      !INERT_SCRIPT_TYPES.has(element.getAttribute('type')?.trim().toLowerCase() ?? '')
    ) {
      return {kind: 'unsupported'}
    }
  }

  const styles: string[] = []
  for (const link of localStylesheets) {
    const rels = (link.getAttribute('rel') ?? '').trim().toLowerCase().split(/\s+/)
    const type = (link.getAttribute('type') ?? '').trim().toLowerCase()
    if (
      rels.length !== 1 ||
      rels[0] !== 'stylesheet' ||
      (type !== '' && type !== 'text/css') ||
      [...link.attributes].some(attribute => !STYLESHEET_ATTRIBUTES.has(attribute.name))
    ) {
      return {kind: 'unsupported'}
    }
    const path = siblingStylesheetPath(entry, link.getAttribute('href') ?? '')
    if (path === undefined) return {kind: 'unsupported'}
    const css = catalog.find(artifact => artifact.absolutePath === path)?.content
    dependencies.set(path, css)
    if (css === undefined || !isSupportedCss(css)) return {kind: 'unsupported'}
    const slot = parsed.createElement('style')
    slot.setAttribute(SLOT_ATTRIBUTE, String(styles.length))
    styles.push(css)
    link.replaceWith(slot)
  }

  const content = parsed.documentElement.outerHTML
  if (isArtifactHtmlPreviewEligible(content) !== true) return {kind: 'unsupported'}
  const html = [...parsed.documentElement.attributes].map(({name, value}): [string, string] => [name, value])
  const body = [...parsed.body.attributes].map(({name, value}): [string, string] => [name, value])
  const inlineScripts = buildStylesheetLoader(styles, html, body)
  return {kind: 'prepared', payload: {content, inlineScripts}, signature: JSON.stringify([content, inlineScripts])}
}

export function buildArtifactHtmlPreview(
  entry: Artifact,
  catalog: readonly Artifact[],
  writesSettled: boolean,
): ArtifactHtmlPreviewPlan {
  return buildPreviewPlan(entry, catalog, writesSettled, new Map())
}

export function cacheArtifactHtmlPreview(
  entry: Artifact,
  catalog: readonly Artifact[],
  writesSettled: boolean,
  previous?: ArtifactHtmlPreviewCacheEntry,
): ArtifactHtmlPreviewCacheEntry {
  if (
    previous?.absolutePath === entry.absolutePath &&
    previous.content === entry.content &&
    previous.writesSettled === writesSettled &&
    [...previous.dependencies].every(
      ([path, content]) => catalog.find(artifact => artifact.absolutePath === path)?.content === content,
    )
  ) {
    return previous
  }
  const dependencies = new Map<string, string | undefined>()
  return {
    absolutePath: entry.absolutePath,
    content: entry.content,
    writesSettled,
    dependencies,
    plan: buildPreviewPlan(entry, catalog, writesSettled, dependencies),
  }
}
